import { create } from "zustand"

// Define the User interface to match actual Kinde data
export interface User {
  id: string
  preferred_email: string
  username: string
  provided_id: string | null // Can be null from Kinde
  last_name: string
  first_name: string
  picture: string | null // Can be null from Kinde
  unit: string
}

// Define the store state and actions interface
interface UserStore {
  user: User

  // Actions
  setUser: (user: User) => void
  updateUser: (updates: Partial<User>) => void
  updateUserField: (field: keyof User, value: string | null) => void
  resetUser: () => void

  // Getter helpers
  getUserId: () => string
  getUserFullName: () => string
  isUserLoggedIn: () => boolean
}

/**
 * Zustand store to manage user state.
 *
 * This store holds the state for user information including id, email, username, name, and other details.
 *
 * @returns {Object} Store with user state and actions
 */
export const useUserStore = create<UserStore>((set, get) => ({
  user: {
    id: "",
    preferred_email: "",
    username: "",
    provided_id: null,
    last_name: "",
    first_name: "",
    picture: null,
    unit: "",
  },

  // Actions
  setUser: (user: User) => set({ user }),

  updateUser: (updates: Partial<User>) =>
    set((state) => ({
      user: { ...state.user, ...updates },
    })),

  updateUserField: (field: keyof User, value: string | null) =>
    set((state) => ({
      user: { ...state.user, [field]: value },
    })),

  resetUser: () =>
    set({
      user: {
        id: "",
        preferred_email: "",
        username: "",
        provided_id: null,
        last_name: "",
        first_name: "",
        picture: null,
        unit: "",
      },
    }),

  // Getter helpers
  getUserId: () => get().user.id,
  getUserFullName: () => {
    const { first_name, last_name } = get().user
    return `${first_name} ${last_name}`.trim()
  },
  isUserLoggedIn: () => get().user.id !== "",
}))
